import { useQuery } from '@tanstack/react-query';
import { formatarMoeda } from '../../componentes/ui';
import { formatarCompetencia } from '../../servicos/formato';
import {
  MODALIDADES,
  carregarCreditosDaUltimaColeta,
  type Modalidade,
} from '../../servicos/prospeccao';

/** O tipo de crédito do Pontual que corresponde a cada modalidade do SISCRED. */
const TIPO_DO_PONTUAL: Readonly<Record<Modalidade, string>> = {
  EXPORTACAO: 'EXPORTACAO',
  DIFERIMENTO: 'DIFERIDO',
  INVESTIMENTO: 'OUTRO',
};

/**
 * Os créditos do fornecedor no SISCRED, na nova negociação do Pontual
 * (29/10/2026): o que o robô coletou da empresa na última coleta em que ela
 * aparece — modalidade, tipo e valor, e o total. Clicar num crédito usa o
 * valor como face e o tipo como tipo de crédito; a negociação pode ser só de
 * parte dele, e o campo continua editável.
 */
export function CreditosNoSiscred(props: {
  readonly fornecedorId: string;
  readonly aoEscolher: (escolha: { readonly valor: number; readonly tipoCredito: string }) => void;
}): JSX.Element | null {
  const creditos = useQuery({
    queryKey: ['prospeccao-creditos', props.fornecedorId],
    queryFn: () => carregarCreditosDaUltimaColeta(props.fornecedorId),
  });

  if (creditos.isPending) {
    return <p className="text-xs text-conteudo-sutil">Buscando os créditos no SISCRED…</p>;
  }
  if (creditos.isError) return null;
  const { coleta, saldos, total } = creditos.data;
  if (coleta === null || saldos.length === 0) {
    return (
      <p className="text-xs text-conteudo-sutil">
        Este fornecedor não aparece em nenhuma coleta do SISCRED.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-conteudo-primario">Créditos no SISCRED</span>
        <span className="text-xs text-conteudo-sutil">
          coleta de {formatarCompetencia(`${coleta.ano}-${String(coleta.mes).padStart(2, '0')}`)}
        </span>
      </div>
      <ul className="flex flex-col divide-y divide-borda/60 text-sm">
        {saldos.map((s, i) => (
          <li key={i}>
            <button
              type="button"
              title="Usar como valor de face"
              onClick={() =>
                props.aoEscolher({ valor: s.valor, tipoCredito: TIPO_DO_PONTUAL[s.modalidade] })
              }
              className="flex w-full items-baseline justify-between gap-3 py-1.5 text-left transition-colors hover:text-acento-ciano"
            >
              <span>
                {MODALIDADES.find((m) => m.chave === s.modalidade)?.rotulo ?? s.modalidade}
                {s.tipo !== null && s.tipo !== '' && (
                  <span className="text-conteudo-secundario"> · {s.tipo}</span>
                )}
              </span>
              <span className="shrink-0 whitespace-nowrap tabular-nums">
                {formatarMoeda(s.valor)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between gap-3 border-t border-borda pt-1.5 text-sm font-semibold">
        <span>Total</span>
        <span className="whitespace-nowrap tabular-nums">{formatarMoeda(total)}</span>
      </div>
      <p className="text-xs text-conteudo-sutil">
        Clique num crédito para usá-lo como valor de face.
      </p>
    </div>
  );
}
